// pages/admin/FeatureAnalytics.tsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Tab,
  Tabs,
  TableSortLabel,
} from "@mui/material";
import { THEME_COLORS } from "../../context/constants";
import { getTemplateAnalytics, TemplateAnalytics } from "../../client/api";

type SortOrder = "asc" | "desc";

export const FeatureAnalytics: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [templateAnalytics, setTemplateAnalytics] = useState<TemplateAnalytics[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const [sortBy, setSortBy] = useState<keyof TemplateAnalytics>("times_used");

  useEffect(() => {
    if (activeTab === 0) {
      fetchTemplateAnalytics();
    }
  }, [activeTab]);

  const fetchTemplateAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getTemplateAnalytics();
      setTemplateAnalytics(data);
    } catch (err) {
      console.error("Error fetching template analytics:", err);
      setError("Failed to load template analytics");
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleSort = (property: keyof TemplateAnalytics) => {
    const isAsc = sortBy === property && sortOrder === "asc";
    setSortOrder(isAsc ? "desc" : "asc");
    setSortBy(property);
  };

  const sortedAnalytics = [...templateAnalytics].sort((a, b) => {
    const aValue = a[sortBy];
    const bValue = b[sortBy];

    if (typeof aValue === "number" && typeof bValue === "number") {
      return sortOrder === "asc" ? aValue - bValue : bValue - aValue;
    }

    if (typeof aValue === "string" && typeof bValue === "string") {
      return sortOrder === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }

    return 0;
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const renderComingSoon = (featureName: string) => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 300,
        p: 4,
      }}
    >
      <Typography variant="h5" color="text.primary" gutterBottom>
        {featureName} Analytics
      </Typography>
      <Typography variant="h6" color={THEME_COLORS.darkYellow}>
        Coming Soon
      </Typography>
      <Typography variant="body2" color="text.primary" sx={{ mt: 1 }}>
        Analytics for this feature are currently in development.
      </Typography>
    </Box>
  );

  const renderTemplatesTab = () => (
    <Box>
      {loading ? (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography color="error">{error}</Typography>
        </Box>
      ) : templateAnalytics.length === 0 ? (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography color="text.primary">No template usage data available yet.</Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ mb: 3 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Summary
              </Typography>
              <Box sx={{ display: "flex", gap: 4 }}>
                <Box>
                  <Typography variant="body2" color="text.primary">
                    Total Templates Used
                  </Typography>
                  <Typography variant="h4">{templateAnalytics.length}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.primary">
                    Total Usage Count
                  </Typography>
                  <Typography variant="h4">
                    {templateAnalytics.reduce((sum, t) => sum + t.times_used, 0)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.primary">
                    Most Used Template
                  </Typography>
                  <Typography variant="h6">
                    {templateAnalytics.length > 0
                      ? templateAnalytics.reduce((max, t) =>
                          t.times_used > max.times_used ? t : max
                        ).template_name
                      : "-"}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === "template_name"}
                      direction={sortBy === "template_name" ? sortOrder : "asc"}
                      onClick={() => handleSort("template_name")}
                    >
                      Template Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === "template_id"}
                      direction={sortBy === "template_id" ? sortOrder : "asc"}
                      onClick={() => handleSort("template_id")}
                    >
                      Template ID
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortBy === "times_used"}
                      direction={sortBy === "times_used" ? sortOrder : "asc"}
                      onClick={() => handleSort("times_used")}
                    >
                      Times Used
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === "updatedAt"}
                      direction={sortBy === "updatedAt" ? sortOrder : "asc"}
                      onClick={() => handleSort("updatedAt")}
                    >
                      Last Used
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedAnalytics.map((template) => (
                  <TableRow key={template.template_id}>
                    <TableCell>{template.template_name}</TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "monospace",
                          fontSize: "0.85rem",
                          color: "text.secondary",
                        }}
                      >
                        {template.template_id}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: "bold",
                          color: template.times_used > 10 ? THEME_COLORS.darkYellow : "inherit",
                        }}
                      >
                        {template.times_used}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatDate(template.updatedAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Feature Analytics
      </Typography>

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        sx={{
          color: THEME_COLORS.yellow,
          "& .MuiTab-root": { color: "black" },
          "& .Mui-selected": { color: THEME_COLORS.yellow },
          mb: 3,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Tab label="Templates" />
        <Tab label="Avatar Lab" />
        <Tab label="Speech to Speech" />
        <Tab label="Others" />
      </Tabs>

      {activeTab === 0 && renderTemplatesTab()}
      {activeTab === 1 && renderComingSoon("Avatar Lab")}
      {activeTab === 2 && renderComingSoon("Speech to Speech")}
      {activeTab === 3 && renderComingSoon("Other Features")}
    </Box>
  );
};

export default FeatureAnalytics;
